import { RouteItem, alertRequiresNavAttention, expected } from '@aloft/toolkit-shared';

import { useCallback } from 'react';

import { useAlertsRef } from '../modules/referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { useModuleContext } from '../modules/shared/moduleContext/useModuleContext.ts';
import { selectCommentaryV2 } from '../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectAlerts } from '../redux/features/selectors/commentarySelectors/selectAlerts.ts';
import { selectIsInspectifyOrder, selectIsMockOrder } from '../redux/features/selectors/orderSelectors.ts';
import { selectTriggeredAlerts } from '../redux/features/selectors/uiSelectors.ts';
import { processAlertsForRoute, retriggerAlertToasts } from '../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../redux/hooks.ts';
import {
  getForwardNavBlockingAlertsForRoute,
  isForwardNavBlockingAlert,
  useAppraisifyNavigation,
} from './validation/useAppraisifyNavigation.ts';

const deferNavigate = (navigate: (path: string) => void, path: string) => {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      navigate(path);
    });
  });
};

export const useAppraisifyNavAlertBlock = () => {
  const dispatch = useAppDispatch();
  const alertsRef = useAlertsRef();
  const isInspectifyOrder = useAppSelector(selectIsInspectifyOrder);
  const isMockOrder = useAppSelector(selectIsMockOrder);
  const triggeredAlerts = useAppSelector(selectTriggeredAlerts);
  const alertsCommentary = useAppSelector(selectCommentaryV2).alerts;
  const dismissedAlerts = useAppSelector(selectAlerts).dismissedAlerts ?? [];
  const finalizedExplanations = useAppSelector((state) => state.ui.finalizedExplanations);
  const explanationContext = { dismissedAlerts, finalizedExplanations };
  const {
    getCurrentAppraisifyRoute,
    getForwardNavigationRedirectRoute,
    isForwardAppraisifyNavigation,
  } = useAppraisifyNavigation();
  const {
    navigation: { useLocation, useNavigate, useParams },
  } = useModuleContext();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { orderId } = useParams();

  const getRedirectRoute = useCallback(
    (targetRoute: RouteItem) => {
      if (!isInspectifyOrder) {
        return undefined;
      }
      const currentRoute = getCurrentAppraisifyRoute(pathname);
      if (!currentRoute || currentRoute === targetRoute) {
        return undefined;
      }
      if (!isForwardAppraisifyNavigation(currentRoute, targetRoute)) {
        return undefined;
      }
      return getForwardNavigationRedirectRoute(currentRoute, targetRoute, alertsRef.current);
    },
    [
      alertsRef,
      getCurrentAppraisifyRoute,
      getForwardNavigationRedirectRoute,
      isForwardAppraisifyNavigation,
      isInspectifyOrder,
      pathname,
    ],
  );

  const wouldBlockForwardNavigation = useCallback(
    (targetRoute: RouteItem) => Boolean(getRedirectRoute(targetRoute)),
    [getRedirectRoute],
  );

  const blockForwardNavigationIfNeeded = useCallback(
    (targetRoute: RouteItem) => {
      const redirectRoute = getRedirectRoute(targetRoute);
      if (!redirectRoute) {
        return false;
      }

      const alerts = alertsRef.current;
      const currentRoute = getCurrentAppraisifyRoute(pathname);
      if (!currentRoute) {
        return false;
      }

      const alertTypesToShow = [
        ...new Set(
          getForwardNavBlockingAlertsForRoute(
            alerts,
            redirectRoute,
            alertsCommentary,
            explanationContext,
          ).map((alert) => alert.type),
        ),
      ];

      if (alertTypesToShow.length > 0) {
        dispatch(retriggerAlertToasts(alertTypesToShow));
      }

      dispatch(
        processAlertsForRoute({
          route: redirectRoute,
          alertsPerSection: alerts,
          alertsCommentary,
          explanationContext,
          openAlertsPanel: redirectRoute !== currentRoute,
        }),
      );

      if (redirectRoute !== currentRoute) {
        deferNavigate(
          (path) => navigate(path, { replace: true }),
          `/order/${expected(orderId)}/paired-cost/${redirectRoute}`,
        );
      }
      return true;
    },
    [
      alertsCommentary,
      alertsRef,
      dispatch,
      explanationContext,
      getCurrentAppraisifyRoute,
      getRedirectRoute,
      navigate,
      orderId,
      pathname,
    ],
  );

  const shouldShowNavAlertTooltip = useCallback(
    (targetRoute: RouteItem, alertCount: number) =>
      isInspectifyOrder &&
      triggeredAlerts.length > 0 &&
      (alertCount > 0 || wouldBlockForwardNavigation(targetRoute)),
    [isInspectifyOrder, triggeredAlerts.length, wouldBlockForwardNavigation],
  );

  const blockCertifyIfNeeded = useCallback(() => {
    if (!isInspectifyOrder || isMockOrder) {
      return false;
    }

    const alerts = alertsRef.current;
    const currentRoute = getCurrentAppraisifyRoute(pathname);
    if (!currentRoute) {
      return false;
    }

    const reconciliationAlerts = getForwardNavBlockingAlertsForRoute(
      alerts,
      'reconciliation',
      alertsCommentary,
      explanationContext,
    );

    if (reconciliationAlerts.length > 0) {
      const alertTypes = reconciliationAlerts.map((alert) => alert.type);
      dispatch(retriggerAlertToasts(alertTypes));
      dispatch(
        processAlertsForRoute({
          route: 'reconciliation',
          alertsPerSection: alerts,
          alertsCommentary,
          explanationContext,
          openAlertsPanel: true,
        }),
      );

      if (currentRoute !== 'reconciliation') {
        deferNavigate(
          (path) => navigate(path, { replace: true }),
          `/order/${expected(orderId)}/paired-cost/reconciliation`,
        );
      }

      return true;
    }

    if (currentRoute !== 'reconciliation') {
      return blockForwardNavigationIfNeeded('reconciliation');
    }

    const triggeredCertBlockingAlertTypes = [
      ...new Set(
        Object.values(alerts)
          .flat()
          .filter(
            (alert) =>
              isForwardNavBlockingAlert(alert) &&
              alertRequiresNavAttention(alert, alertsCommentary, explanationContext) &&
              triggeredAlerts.includes(alert.type),
          )
          .map((alert) => alert.type),
      ),
    ];

    if (triggeredCertBlockingAlertTypes.length > 0) {
      dispatch(retriggerAlertToasts(triggeredCertBlockingAlertTypes));
      return true;
    }

    return false;
  }, [
    alertsCommentary,
    explanationContext,
    alertsRef,
    blockForwardNavigationIfNeeded,
    dispatch,
    getCurrentAppraisifyRoute,
    isInspectifyOrder,
    isMockOrder,
    navigate,
    orderId,
    pathname,
    triggeredAlerts,
  ]);

  return { blockForwardNavigationIfNeeded, blockCertifyIfNeeded, shouldShowNavAlertTooltip };
};
